/**
 * Construção de DOM sem framework.
 *
 * A casca é pequena e a tela do simulador (o próximo lote) traz o JS da v67, que
 * manipula DOM direto. Introduzir um framework aqui criaria dois mundos convivendo no
 * mesmo documento — este arquivo é a alternativa: ~60 linhas que fazem o necessário.
 */

type Filho = Node | string | number | null | undefined | false;

type Props = Record<
  string,
  string | number | boolean | null | undefined | EventListenerOrEventListenerObject
>;

/**
 * Cria um elemento. Chaves começando com `on` viram ouvintes; `class` vira className;
 * `html` injeta marcação (só para ícone declarado no código, nunca para texto vindo do
 * banco — este é o motivo de existir o `texto()` abaixo).
 */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Props = {},
  ...filhos: Filho[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(props)) {
    if (v === null || v === undefined || v === false) continue;
    /* TOOLTIP VAZIA NÃO ENTRA (07/08/2026). `title=""` não mostra caixa nenhuma, mas
       SUPRIME o tooltip do ancestral e polui a marcação com uma dica que não diz nada.
       O padrão certo já existia no legado (`item()` do menu do GRID só escreve o atributo
       quando há dica); aqui a regra passa a valer para toda a casca, em vez de depender
       de cada chamador lembrar de mandar `null` no ramo sem texto. */
    if (k === 'title' && String(v).trim() === '') continue;
    if (k.startsWith('on') && typeof v !== 'string' && typeof v !== 'number' && typeof v !== 'boolean') {
      el.addEventListener(k.slice(2).toLowerCase(), v);
    } else if (k === 'class') {
      el.className = String(v);
    } else if (k === 'html') {
      el.innerHTML = String(v);
    } else if (v === true) {
      el.setAttribute(k, '');
    } else {
      el.setAttribute(k, String(v));
    }
  }
  poe(el, filhos);
  return el;
}

/** Anexa filhos, ignorando `null`/`false` — deixa `cond && h(...)` funcionar no lugar. */
export function poe(pai: Node, filhos: readonly Filho[]): void {
  for (const f of filhos) {
    if (f === null || f === undefined || f === false) continue;
    pai.appendChild(typeof f === 'string' || typeof f === 'number' ? document.createTextNode(String(f)) : f);
  }
}

/** Texto vindo do banco entra por aqui — nunca por `html`. */
export function texto(t: string): Text {
  return document.createTextNode(t);
}

export function limpar(el: Element): void {
  el.replaceChildren();
}

export function achar<T extends Element = HTMLElement>(sel: string): T | null {
  return document.querySelector<T>(sel);
}

/* ============================================================================
 * Formatação — reusa a do motor, que é a mesma fonte da v67
 * ========================================================================== */

const DATA_CURTA = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', year: '2-digit' });

/** `2026-07-26T…` → `26 de jul. de 26`, no padrão que a v67 já usa nas listas. */
export function dataCurta(iso: string | null): string {
  if (iso === null || iso === '') return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : DATA_CURTA.format(d);
}

/** Hora do relógio, para "salvo às 14:32". */
export function hora(d: Date): string {
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}
